/**
 * /research/baby-chops-v2 — "Baby Chops v2" tool-detail page.
 *
 * Preview of the Chrome-Web-Store-distributed install flow for Baby Chops,
 * a standalone Chrome extension that pulls people and company data off a
 * LinkedIn Recruiter advanced-search results page and maps it into an
 * import.
 *
 * Distribution: Baby Chops is its OWN Chrome Web Store listing — separate
 * from the nu-assist extension — with one listing PER STAGE ("Baby Chops
 * (Dev)" and "Baby Chops"). It's scraper-only (no Thrive surface), installed
 * directly from its own CWS listing rather than folded into nu-assist or
 * force-installed via Google Workspace. The panel is auto-injected on
 * LinkedIn Recruiter results (not a toolbar popup). Available to every
 * recruiter (Jose, 2026-08-11) — this page must not imply otherwise.
 *
 * RELEASED 2026-08-10 (delivery phase 1), and it is now THE Baby Chops page:
 * the Drive/Load-unpacked v1 page (`BabyChopsTool`, `/research/baby-chops`)
 * was deleted and that path redirects here (`LEGACY_REDIRECTS` in
 * `routes.tsx`). The sidebar row reads plain "Baby Chops" and carries an
 * "updated" pill rather than a "new" one — the tool is not new to the reader,
 * its install flow is.
 *
 * The local-dev `linkedin-mock` fixture section below was carried over from
 * the deleted v1 page. It is developer tooling with no v2 equivalent, so
 * letting it die with v1 would have quietly removed the only way to exercise
 * the scrape path without a real LinkedIn Recruiter session.
 *
 * SIR CHOPS AND THE TIPS (2026-10-08, epic
 * `2026-10-08-candidates-polish-thrive-avatar-worker`, sub-task 04). The header
 * shows Lauren's Sir Chops with the NU shield (`/choplink-sir-chops.png`, a
 * 512 px square) at its own ratio, `object-contain`, because she asked that he
 * never be stretched. Since 2026-10-08 (Jose) he sits at 176 px in the first
 * card with the name, the pill and the description beside him. The Tips card
 * under How to use carries Jared's "how to chop" advice (Trello S3iUyvzy),
 * headed by a light bulb. `/baby-chops.png` stays in `public/`: nothing on the web renders
 * it any more, but it is the asset path the 2026-09-04 renames kept as an
 * internal id, and an old link to it should not 404.
 *
 * Content-only page: the authenticated shell is provided by `WebShellLayout`.
 */
import { Download, Lightbulb } from "lucide-react";
import {
  CHOPLINK_GUIDE,
  getCwsListingUrl,
  useLatestExtensionVersion,
  useMe,
} from "@nu-assist/shared-fe";
import { resolveStage } from "../../config/featureFlags";
import { BABY_CHOPS_MANUAL_INSTALL_NOTICE, isAnnouncementActive } from "../../shell/leftMenuModel";
import { ExtensionInstallSection } from "./ExtensionInstallSection";

const INSTALL_STEPS = [
  "Sign into the Chrome Web Store with your @nusearch.com account.",
  "Install ChopLink from its own Chrome Web Store listing.",
  "Chrome keeps it up to date automatically once installed.",
];

// Port 7100 = host-side mapping of linkedin-mock container:8000
// (docker-compose.yml). Override via LINKEDIN_MOCK_PORT in .env if needed
// (update this constant to match). Carried over from the deleted v1 page.
const LINKEDIN_MOCK_BASE = "http://localhost:7100";
const IS_LOCAL = import.meta.env.VITE_APP_STAGE === "local";

// Shared with the extension (panel + popup) so the wording cannot drift.
const { howToSteps: HOW_TO_STEPS, tips: TIPS } = CHOPLINK_GUIDE;

export function BabyChopsV2Tool() {
  const me = useMe();
  // Listing URL comes from `shared-fe`'s per-{stage, extension} table (see its
  // docstring for the grammar); it was previously a module constant pinned to
  // the DEV listing, which sent prod recruiters to a listing they are not
  // testers on.
  //
  // `resolveStage()` is the WEB app's 2-valued resolver, which collapses
  // `local` → `dev` deliberately: "Baby Chops (Local)" is a Load-Unpacked
  // build with no listing, so a local developer wants the DEV link rather than
  // a dead one. Same call shape as `SequencesPage`'s Install NUCCE CTA.
  const cwsListingUrl = getCwsListingUrl(resolveStage(), "baby-chops");
  // Live latest-version read for the Baby Chops listing — mirrors how the
  // Outreach > Sequences page surfaces the NUCCE version, but scoped to the
  // Baby Chops extension via the extension-aware endpoint. Fails silently to
  // null (listing pre-launch / API down) → the version line simply hides.
  const latest = useLatestExtensionVersion({ extension: "baby-chops" });
  const availableVersion =
    latest.data?.current.available?.manifest_version ??
    latest.data?.current.coming_soon?.manifest_version ??
    null;

  // Defensive narrow — WebShellLayout's AuthGuard already wraps this.
  if (me.status !== "authenticated") return null;

  return (
    <div data-testid="tool-babychops-v2" className="p-12">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <nav aria-label="Breadcrumb" className="mb-2 text-caption text-text-secondary">
          Research &amp; Sourcing <span className="px-1">/</span>
          {/* Reads "Baby Chops", not "Baby Chops v2": the id and path keep the
              `-v2` suffix (they key featureFlags + PHASE_PAGE_IDS + the
              redirect), but there is only one Baby Chops from the reader's
              side now, so the visible name matches the sidebar row. */}
          <span className="text-accent">ChopLink</span>
        </nav>
        <div className="flex flex-col gap-5">
          {/* The extension itself (Jose, 2026-10-08): Sir Chops at three times
              the old 56 px header size, the name and its pill as the title, and
              the one-line description under it. Square asset, `object-contain`:
              the image keeps its own ratio and is never stretched (Lauren). */}
          <section
            data-testid="tool-babychops-v2-hero"
            className="flex flex-wrap items-center gap-6 rounded-md border border-border bg-surface p-5"
          >
            <img
              src="/choplink-sir-chops.png"
              alt=""
              aria-hidden
              className="h-44 w-44 shrink-0 rounded-md object-contain"
            />
            <div className="flex min-w-0 flex-1 basis-60 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-h1 text-text">ChopLink</h1>
              </div>
              <p className="text-body text-text-secondary">
                Pull people and company data from LinkedIn Recruiter based on the advanced search
                results page.
              </p>
            </div>
          </section>

          {/* Install — shared with the Chopbook Tool page.

              The "Rolling out: currently Private and limited to testers"
              caveat that used to sit here was REMOVED 2026-08-11. Baby Chops
              is accessible to every recruiter (Jose), so the block was both
              false and self-defeating — it told a reader they could not yet
              install a thing the button directly below installs.

              Baby Chops carried a Drive-ZIP / Load-Unpacked install for months
              before the CWS listing (the retired v1 page handed the ZIP out),
              so a real population of recruiters is still on a manual copy that
              will never update — hence the notice the shared section renders.

              The notice is TIME-BOXED (2026-08-14, operator directive): it
              runs for `BABY_CHOPS_MANUAL_INSTALL_NOTICE`'s window and then
              goes quiet, because the population it addresses is whoever has
              not yet read it, and that shrinks to nothing. Chopbook dropped
              its copy outright in the same change. Re-dated 2026-09-23 with
              the prod delivery (30-day window, so quiet from 2026-10-23) —
              and this is the run that reaches the population it describes:
              until that day prod recruiters WERE the manual-install copy,
              because v1 was their only page. Re-date the constant to bring it
              back. */}
          <ExtensionInstallSection
            extensionName="ChopLink"
            testIdPrefix="tool-babychops-v2"
            cwsListingUrl={cwsListingUrl}
            availableVersion={availableVersion}
            steps={INSTALL_STEPS}
            showManualInstallNotice={isAnnouncementActive(BABY_CHOPS_MANUAL_INSTALL_NOTICE)}
            noListingCopy="ChopLink has no Chrome Web Store listing on this environment — load it unpacked from a local build instead."
          >
            ChopLink is its own{" "}
            <strong className="font-semibold text-text">Chrome extension</strong>, with its own
            Chrome Web Store listing — it does not ship inside the nu-assist extension. Install it
            directly from that listing; Chrome keeps it up to date after that.
          </ExtensionInstallSection>

          {/* How to use */}
          <section className="rounded-md border border-border bg-surface p-5">
            <h2 className="mb-2 text-h3 text-text">How to use</h2>
            <ol className="list-decimal space-y-1 pl-5 text-body text-text-secondary">
              {HOW_TO_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </section>

          {/* Tips */}
          <section
            data-testid="tool-babychops-v2-tips"
            className="rounded-md border border-border bg-surface p-5"
          >
            <h2 className="mb-2 flex items-center gap-2 text-h3 text-text">
              <Lightbulb className="h-5 w-5 text-warning-ink" aria-hidden="true" />
              Tips
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-body text-text-secondary">
              {TIPS.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </section>

          {/* Local dev shortcut — only visible when linkedin-mock is running.
              Carried over verbatim from the deleted v1 page; it is the only
              way to exercise the scrape path without a LinkedIn Recruiter
              session, and it has no v2 equivalent. */}
          {IS_LOCAL && (
            <section className="rounded-md border border-dashed border-accent bg-accent-muted p-5">
              <h2 className="mb-1 text-h3 text-text">Local dev fixture (linkedin-mock)</h2>
              <p className="mb-3 text-body text-text-secondary">
                Download a synthetic fixture CSV directly from the local linkedin-mock service — no
                LinkedIn Recruiter session required.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`${LINKEDIN_MOCK_BASE}/download?company=Acme+Search+Partners`}
                  download="linkedin-mock-acme-search-partners.csv"
                  className="inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-label font-bold text-bg hover:bg-accent-hover"
                >
                  <Download size={14} aria-hidden />
                  Download fixture CSV
                </a>
                <a
                  href={`${LINKEDIN_MOCK_BASE}/companies`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-label font-bold text-text hover:bg-accent-muted"
                >
                  Browse companies
                </a>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
